import {
  Button,
  Divider,
  Grid,
  lighten,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { graphql } from 'gatsby';
import React from 'react';
import Helmet from 'react-helmet';
import Link from '../../components/link';
import PageLayout from '../../components/page-layout';
import Section from '../../components/section';

export const query = graphql`
  query blogListingsPageQuery($skip: Int!, $limit: Int!) {
    allMdx(
      sort: { fields: [frontmatter___date], order: DESC }
      filter: { fileAbsolutePath: { glob: "**/blog/**/*.mdx" } }
      limit: $limit
      skip: $skip
    ) {
      edges {
        node {
          excerpt(pruneLength: 200)
          frontmatter {
            title
            author
            slug
            date(formatString: "YYYY-MM-DD")
          }
        }
      }
    }
  }
`;

const useStyles = makeStyles(theme => ({
  searchField: {
    backgroundColor: '#fff',
  },
  listItem: {
    padding: theme.spacing(6, 0),
  },
  postTitle: {
    marginBottom: theme.spacing(1),
  },
  postMetadata: {
    color: lighten(theme.palette.text.primary, 0.4),
  },
  readMoreLink: {
    display: 'block',
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
  },
}));

type BlogListingsLayoutProps = {
  pageContext: {
    current_page: number;
    num_pages: number;
  };
  data: {
    allMdx: {
      edges: Array<{
        node: {
          excerpt: string;
          frontmatter: {
            title: string;
            slug: string;
            author: string;
            date: string;
          };
        };
      }>;
    };
  };
};

const BlogListingsLayout: React.FC<BlogListingsLayoutProps> = props => {
  const classes = useStyles();
  const { current_page, num_pages } = props.pageContext;
  const posts = props.data.allMdx.edges.map(edge => ({
    ...edge.node.frontmatter,
    excerpt: edge.node.excerpt,
  }));
  return (
    <PageLayout showFooterHero={false}>
      <Helmet title="Blog" />
      <Section maxWidth="sm" variant="hero-green">
        <Typography variant="h1">Architect Blog</Typography>
      </Section>

      <Section maxWidth="md" verticalSpacing={8}>
        <List>
          {posts.map((post, index) => (
            <div key={index}>
              <ListItem className={classes.listItem}>
                <ListItemText
                  primary={
                    <Link
                      to={`/blog/${post.date}/${post.slug}/`}
                      styled="true"
                      className={classes.postTitle}
                    >
                      <Typography variant="h3">{post.title}</Typography>
                    </Link>
                  }
                  secondary={
                    <>
                      <Typography variant="body1" paragraph={false}>
                        {post.excerpt}
                      </Typography>
                      <Typography variant="overline" className={classes.postMetadata}>
                        {post.author} - {post.date}
                      </Typography>
                    </>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
              {posts.length - 1 > index ? <Divider /> : ''}
            </div>
          ))}
        </List>

        <Grid container justifyContent="space-between">
          <Grid item>
            {current_page !== 1 && (
              <Link to={`/blog/${current_page === 2 ? '' : current_page - 1}/`}>
                <Button color="primary" startIcon={<ArrowBackIcon />}>
                  Newer posts
                </Button>
              </Link>
            )}
          </Grid>
          <Grid item>
            {current_page !== num_pages && (
              <Link to={`/blog/${current_page + 1}/`}>
                <Button color="primary" endIcon={<ArrowForwardIcon />}>
                  Older posts
                </Button>
              </Link>
            )}
          </Grid>
        </Grid>
      </Section>
    </PageLayout>
  );
};

export default BlogListingsLayout;
